import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartInvoicePrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Invoice Maker for Contractors"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how the mobile application “Smart Invoice
        Maker” (the “App”) collects, uses, stores, and discloses information.
        The App is designed to let you create and send professional invoices and
        estimates from your phone, operate offline without account registration,
        and export documents as PDF for sharing. This Policy covers
        invoice/estimate creation, local storage of document data, PDF preview
        and export, optional notifications, subscriptions and entitlements,
        analytics and crash diagnostics, and support. We follow a local-first
        approach so your documents remain on your device unless you explicitly
        export or share them. By using the App, you acknowledge this Policy and
        agree to its terms. If you do not agree, uninstall the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller determining purposes and means of processing is
        Aleksandra Rakitina. For privacy questions or to exercise rights such as
        access, deletion, correction, restriction, objection, or portability,
        contact alexwadek@gmail.com. We verify identity before acting on
        requests, typically by confirming control of the email you use to
        contact us or by requesting minimal additional information. We retain
        privacy correspondence for compliance and security documentation where
        allowed by law. If your request concerns platform-level subscription
        settings or refunds, we will direct you to the relevant device account
        settings while addressing privacy matters within our remit.
      </p>

      <div className={s.title}>
        3) Key Principles (Local-First, Minimization, User Control)
      </div>
      <p>
        Local-first: invoice data, estimate data, and settings are stored on
        your device in app-scoped storage and are not uploaded to our servers.
        Minimization: we collect only what is necessary to deliver functionality
        such as subscriptions, stability analytics, and crash diagnostics; we do
        not sell Personal Data. User control: you decide what to enter, what to
        export, which currency/tax options to enable, and when to delete items.
        Cloud sync is not part of the MVP and is not required to use the App.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Document Data” includes the fields you enter into
        invoices and estimates (e.g., business name, client name/company, email,
        line items, quantities, prices, taxes, discounts, totals). “Local
        Metadata” means on-device information such as document IDs, dates,
        currency settings, tax toggle, and logo references. “Processing”
        includes collection, storage, use, transformation (PDF generation),
        export, and deletion. “Service Providers/Processors” are third parties
        acting on our instructions for subscriptions, analytics, crash
        reporting, and notifications.
      </p>

      <div className={s.title}>5) What We Do Not Collect</div>
      <p>
        We do not operate a backend for storing your invoices/estimates. We do
        not upload Document Data or attached logos to our servers. We do not
        collect payment card numbers. We do not require account registration to
        use core features. We do not share your Document Data with advertising
        networks and do not sell Personal Data. If you export or share a
        PDF/TXT via other apps or services, those destinations have their own
        privacy practices outside our control.
      </p>

      <div className={s.title}>6) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: (a) Document Data you input
        (business name, client details, line items, taxes, discounts, totals)
        stored locally; (b) Local Metadata (document IDs, dates, currency code,
        tax setting, file paths for logos); (c) Subscription and entitlement
        metadata from RevenueCat (product identifiers, trial status, renewal
        outcome, expiration/cancellation) to grant premium access; (d)
        Analytics and crash diagnostics (app/OS version, device model,
        anonymized error codes, event counters like invoice_created,
        invoice_exported, paywall_opened, trial_started, purchase_success); (e)
        Notification tokens and preferences if you enable reminders via system
        notifications. We do not receive payment card details.
      </p>

      <div className={s.title}>7) Sources of Data and Voluntary Provision</div>
      <p>
        Data originates from you when you enter business/client details, line
        items, discounts, and taxes, and when you configure currency and logo
        settings. Additional technical data arises automatically from App
        operation (e.g., event counters and crash logs used to improve
        reliability). Certain features require permissions or inputs: without
        file/media access you cannot add a logo; without network access
        subscriptions and updates to SDKs may be limited; without notification
        permission, reminder alerts cannot be delivered. You can revoke
        permissions in system settings at any time.
      </p>

      <div className={s.title}>
        8) Invoice/Estimate Creation and Local Storage
      </div>
      <p>
        Invoices and estimates are created and stored locally using an
        app-scoped database (e.g., Hive). You can view, duplicate, and delete
        documents from the main list. Deleting a document removes its local
        record and associated file references. Uninstalling the App deletes the
        app sandbox and stored data. If you previously exported PDFs to external
        destinations (e.g., email, cloud drive, messaging apps), those copies
        remain under those services’ control.
      </p>

      <div className={s.title}>9) PDF Preview, Export, and Sharing</div>
      <p>
        When you choose “Preview” or “Save as PDF,” the App generates a PDF
        using a template containing your logo (if enabled), header (Invoice or
        Estimate), line items, subtotals, taxes/discounts, and totals. Export
        saves the PDF to a location you select or shares it via system share
        sheets (e.g., Email, WhatsApp, AirDrop). Exported files leave the App’s
        sandbox and are governed by the destination’s privacy practices.
        Deleting a document inside the App does not delete exported copies;
        manage those in the destination app/service.
      </p>

      <div className={s.title}>10) Logos and Media Access</div>
      <p>
        If you add a logo from your gallery/files, the App requests read access
        to retrieve the selected image. The App references the logo for document
        rendering and stores only the minimal path/identifier in Local
        Metadata. We do not scan your photo library. Removing a logo in Settings
        or deleting the image from its source path may affect how future
        previews render; you can re-select a logo at any time.
      </p>

      <div className={s.title}>
        11) Taxes, Discounts, and Currency Settings
      </div>
      <p>
        You can configure percentage-based tax and discount fields and choose a
        display currency (e.g., $, €, £). These settings are stored locally and
        applied to calculations when you create documents. The App performs
        basic arithmetic for subtotals, tax, and totals; it does not provide
        financial, accounting, or tax advice. Verify values before sending
        documents to clients and consult professionals for specific tax
        requirements in your jurisdiction.
      </p>

      <div className={s.title}>12) Free Tier Limits and Paywall</div>
      <p>
        The free tier may allow up to three saved invoices/estimates and may
        disable logo usage. When you reach the free limit or attempt
        premium-only actions, the paywall is shown. Paid access (via
        invoice_premium_weekly) unlocks unlimited documents and logo support,
        subject to fair-use principles designed to keep storage and performance
        reasonable.
      </p>

      <div className={s.title}>
        13) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Subscriptions are handled by platform billing via RevenueCat. The weekly
        plan is currently $4.99/week with a 3-day free trial. We receive
        subscription metadata (product IDs, trial eligibility, renewal results,
        expiration dates, cancellation status) to grant or revoke access. We do
        not receive payment card numbers. Manage or cancel subscriptions in your
        device account settings; refunds and grace periods follow platform rules
        and applicable law. Entitlement checks occur on-device using provider
        SDKs; occasional connectivity is required to refresh status.
      </p>

      <div className={s.title}>
        14) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to measure stability and
        improve performance, not to build behavioral advertising profiles.
        Typical events: invoice_created, invoice_exported, paywall_opened,
        trial_started, purchase_success. Where feasible, analytics are
        aggregated or pseudonymized, and we avoid sending full Document Data in
        event payloads. You can reduce analytics using platform privacy settings
        where available, understanding this may limit our ability to troubleshoot
        rare device-specific issues.
      </p>

      <div className={s.title}>
        15) Notifications and In-App Messages (Optional)
      </div>
      <p>
        If you enable notifications, the App may send local or push reminders
        (e.g., “Finish your invoice draft”). Delivery requires a device token
        (for push) or local scheduling. You can disable notifications in system
        settings and, where offered, in-app toggles. We avoid placing client
        emails or sensitive amounts in notification bodies. Uninstalling the App
        removes local schedules and typically invalidates push tokens, though
        short delays may occur before full deactivation.
      </p>

      <div className={s.title}>
        16) What We Share with Service Providers
      </div>
      <p>
        To operate the App, we share limited data with vetted providers acting on
        our instructions: RevenueCat (subscriptions and entitlements), Firebase
        Analytics/Crashlytics (measurement and stability), and notification
        services where enabled. Providers are bound by terms requiring
        appropriate security and prohibiting use of Personal Data for their
        independent marketing. We do not upload your Document Data (invoice
        contents) to these providers. If required by law, we may disclose
        limited information to competent authorities after validating scope and
        jurisdiction.
      </p>

      <div className={s.title}>17) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards
        designed to protect data: HTTPS for relevant communications;
        app-sandboxed local storage; least-privilege access to operational
        tools; credential rotation; separation of development and production; and
        avoiding use of production Personal Data for testing. While no system is
        perfectly secure, our local-first design reduces exposure by keeping
        Document Data on your device unless you export it.
      </p>

      <div className={s.title}>18) Data Retention and Deletion</div>
      <p>
        Local data (documents, settings, logo references) remains on your device
        until you delete it or uninstall the App. Subscription metadata may be
        retained by providers for accounting and fraud prevention as permitted
        by law, typically in pseudonymized or aggregated form. Analytics/crash
        diagnostics are retained for operational trend windows and then deleted
        or de-identified. Verified deletion requests for server-side records we
        control (e.g., support emails) are handled within a reasonable
        timeframe, acknowledging that backups and replication can introduce short
        delays before complete purge.
      </p>

      <div className={s.title}>19) International Data Transfers</div>
      <p>
        Because the App stores documents locally by default, international
        transfers of Document Data to our servers do not occur. Limited Personal
        Data (e.g., subscription or analytics metadata) may be processed by
        providers in other jurisdictions. Where required, we rely on appropriate
        safeguards such as contractual protections and platform mechanisms for
        cross-border transfers. Providers must implement security measures
        consistent with this Policy and applicable law.
      </p>

      <div className={s.title}>20) Your Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, delete, or
        port Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app, you can
        edit or delete documents, change currency/tax settings, remove logos, and
        clear recent items. Subscription management (including cancellation and
        refunds) occurs in platform account settings. To exercise other rights,
        email alexwadek@gmail.com from your contact address; we will verify
        identity and respond within a reasonable time.
      </p>

      <div className={s.title}>
        21) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers needed for subscriptions, analytics, crash reporting, and
        notifications. These identifiers function similarly to cookies by
        enabling sessions, entitlement checks, and measurement. The App does not
        rely on advertising identifiers for targeted ads. Browser “Do Not Track”
        signals generally do not apply to native apps; we keep SDK usage limited
        to necessary purposes and avoid combining identifiers across services for
        unrelated marketing.
      </p>

      <div className={s.title}>
        22) Accuracy, Business Responsibility, and Limitations
      </div>
      <p>
        You are responsible for ensuring the accuracy of invoice/estimate
        details, tax rates, discounts, and client contact information before
        sending documents. The App performs basic calculations and PDF formatting
        but does not provide legal, accounting, or tax advice. Regulations and
        invoice requirements vary by jurisdiction; consult a professional for
        specific guidance. This section concerns product expectations and does
        not limit your statutory privacy rights elsewhere in this Policy.
      </p>

      <div className={s.title}>23) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. If you believe a minor used the App without appropriate
        authorization, contact alexwadek@gmail.com and we will remove
        information and terminate access where applicable. Device-level controls
        can help limit purchases and notifications.
      </p>

      <div className={s.title}>
        24) Third-Party Links and External Destinations
      </div>
      <p>
        If you open links to documentation, support pages, or platform settings,
        you leave the App environment and the destination’s privacy practices
        apply. Exported PDFs shared via email, messaging, or cloud drives are
        governed by those services’ policies. Review permission prompts and
        terms before sharing sensitive client details. Deleting a document in the
        App does not remove exported copies from external destinations.
      </p>

      <div className={s.title}>
        25) Platform Compliance and Review Transparency
      </div>
      <p>
        For clarity to users and reviewers, we disclose that documents are stored
        locally by default; logo usage is optional and local; subscriptions are
        handled via platform billing and RevenueCat; analytics/crash reports are
        oriented to stability; and a paywall may appear after free-tier limits
        (e.g., more than three saved documents). Any future feature that changes
        data flows (e.g., optional cloud backup) will be disabled by default and
        accompanied by in-app disclosures and an updated Policy before
        activation.
      </p>

      <div className={s.title}>
        26) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        For EEA/UK users, legal bases include performance of a contract (core
        document creation and export), legitimate interests (security, fraud
        prevention, stability and improvement) balanced against your rights
        through minimization and controls, consent (optional notifications and
        certain analytics categories where required), and legal obligations
        (records and compliance). Globally, we apply similar principles
        regardless of location and will seek additional consent if purposes
        materially change.
      </p>

      <div className={s.title}>27) Security Incident Response</div>
      <p>
        If we become aware of a security incident affecting Personal Data we
        control (e.g., subscription or support records), we will investigate
        promptly, take appropriate remediation steps, and notify affected users
        and authorities where required by law. Because documents are local by
        default and not uploaded to our servers, the scope of any remote incident
        is generally limited to non-content data.
      </p>

      <div className={s.title}>28) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes that affect your rights or our processing will be
        highlighted in-app, and where required we will seek consent for new
        uses. Continued use of the App after the effective date signifies
        acceptance. If you disagree with updates, stop using the App and request
        deletion of any account-linked records we control, subject to
        verification and applicable retention obligations.
      </p>

      <div className={s.title}>
        29) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com and specify the
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model and app
        version. We verify identity to prevent unauthorized access or deletion
        and may request limited additional details or a confirmation code. Where
        feasible, data is provided in a portable, machine-readable format. We
        may decline or limit requests when permitted by law (for example, if
        fulfilling a request would compromise security, interfere with legal
        obligations, or infringe others’ privacy).
      </p>
    </PolicyLayout>
  );
}
